import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import LandingPage from './components/LandingPage';
import AssistantWindow from './components/AssistantWindow';
import ErrorBoundary from './components/ErrorBoundary';
import PortalGate from './components/PortalGate';
import LoginGate from './components/LoginGate';
import { IS_SINGLE_EDGE } from './serverApi';
import { readView, subscribe, takeEnterDirection, VIEW_ASSISTANT, VIEW_CONSOLE } from './appView';

// Root picks one of three surfaces. They render in place of one another rather
// than nesting, so moving between them is a pushState and a re-render — no page
// load and no popup window. Deep links (?view=console, ?view=assistant) skip
// the chooser; a bare URL lands on it.
// How long .surface-enter animates for; the class is dropped after it so the
// surface goes back to ordinary document flow.
const SLIDE_MS = 340;

// The gate wraps ALL three surfaces, not just the Console.
//
// It used to sit inside App, which left the landing chooser and Ada reachable
// without a session: a signed-out visitor got the "how do you want to drive the
// network" page, and only hit a login screen after choosing Console. Ada was
// worse — a deep link to ?view=assistant opened the assistant outright.
//
// Hoisting it here makes authentication the first thing that happens, so every
// surface is behind one session instead of each being responsible for its own.
// It also sits OUTSIDE the keyed transition wrapper below, so switching
// surfaces re-renders without remounting the gate and re-checking auth.
const Gate = IS_SINGLE_EDGE ? LoginGate : PortalGate;

function Root() {
  const [view, setView] = useState(readView);
  const [enter, setEnter] = useState('');

  useEffect(() => subscribe(() => {
    setEnter(takeEnterDirection());
    setView(readView());
  }), []);

  // Drop the transition wrapper once it has played. Left on, its transform
  // keeps acting as the containing block for the surface's fixed-position
  // shell, and its fixed geometry keeps the console from scrolling.
  useEffect(() => {
    if (!enter) return undefined;
    const t = setTimeout(() => setEnter(''), SLIDE_MS);
    return () => clearTimeout(t);
  }, [enter, view]);

  // Ada and the Console are the same document rendering something else, so
  // without a transition the switch reads as a page break. Sliding in the
  // direction of travel says the two are neighbours, which is what they are.
  const surface = view === VIEW_ASSISTANT ? <AssistantWindow />
    : view === VIEW_CONSOLE ? <App />
    : <LandingPage />;

  // A crash here used to paint a blank page, which tells the operator nothing
  // and leaves them no way back.
  return (
    <ErrorBoundary>
      <Gate>
        <div className={enter ? `surface-enter surface-enter--${enter}` : undefined}
             key={view}>
          {surface}
        </div>
      </Gate>
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Register the PWA service worker so the console is installable (Add to Home
// Screen) and the app shell works offline. No-op on insecure origins or
// browsers without service-worker support — registration simply fails quietly.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const base = process.env.PUBLIC_URL || '';
    navigator.serviceWorker
      .register(`${base}/service-worker.js`, { scope: `${base}/` })
      .catch(() => {});
  });
}
